import { ReactNode } from 'react';
import { useLightestOutline } from './use-lightest-outline';
import { useCardTitleColor } from './use-card-title-color';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

interface OptionCardProps {
  icon?: ReactNode;
  title: string;
  bestFor?: string;
  selected?: boolean;
  children: ReactNode;
  actions: ReactNode;
}

export default function OptionCard({
  icon,
  title,
  bestFor,
  selected = false,
  children,
  actions,
}: OptionCardProps) {
  const theme = useTheme();
  const lightestOutline = useLightestOutline();
  const titleColor = useCardTitleColor();

  return (
    <Stack
      gap={3}
      alignItems="flex-start"
      sx={{
        flex: 1,
        minWidth: 0,
        p: 3,
        borderRadius: 2,
        border: '1px solid',
        borderColor: selected
          ? theme.palette.semantic.outline.primary
          : lightestOutline,
        backgroundColor: theme.palette.semantic.surface.bg2,
      }}
    >
      {icon && (
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            backgroundColor: theme.palette.semantic.surface.bg1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      )}
      <Stack gap={2} sx={{ width: '100%', flex: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: titleColor }}>
            {title}
          </Typography>
          {bestFor && (
            <Typography
              variant="caption"
              sx={{ color: theme.palette.semantic.text.disabled }}
            >
              {bestFor}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            fontSize: 16,
            lineHeight: 1.5,
            color: theme.palette.semantic.text.support,
            '& p': { m: 0 },
            '& ol, & ul': { m: 0, pl: 3 },
          }}
        >
          {children}
        </Box>
      </Stack>
      {actions}
    </Stack>
  );
}
